import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, HandCoins, Copy, EyeOff, QrCode } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { SupportQrDialog } from '../SupportQrDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContributionsView() {
  const [data, setData] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/support/contributions`).then(r => setData(r.data));
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/support`);
    toast({ title: 'Support link copied', description: `${window.location.origin}/support` });
  };

  if (data === null) return <div className="h-full grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

  const total = data.items.reduce((s, i) => s + i.amount, 0);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-serif text-stone-900">Contributions</h1>
          <div className="text-[12px] text-stone-500">{data.items.length} contribution{data.items.length === 1 ? '' : 's'} · ${total.toFixed(2)} total (donors shown by initials only)</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="contributions-qr-btn" onClick={() => setQrOpen(true)}><QrCode className="w-3.5 h-3.5 mr-1.5" /> QR code</Button>
          <Button variant="outline" data-testid="copy-support-link-btn" onClick={copyLink}><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy support page link</Button>
        </div>
      </div>
      <SupportQrDialog open={qrOpen} onOpenChange={setQrOpen} />
      <div className="flex-1 overflow-auto p-5">
        {!data.enabled ? (
          <Card className="border-stone-200"><CardContent className="p-10 text-center text-stone-500">
            <EyeOff className="w-8 h-8 mx-auto mb-2 text-stone-300" />
            <div className="text-[14px]">The contributions list is turned off in Settings.</div>
          </CardContent></Card>
        ) : data.items.length === 0 ? (
          <Card className="border-stone-200"><CardContent className="p-10 text-center text-stone-500">
            <HandCoins className="w-8 h-8 mx-auto mb-2 text-stone-300" />
            <div className="text-[14px] mb-1">No contributions yet</div>
            <div className="text-[12px]">Share the support page link in your thank-you and assets emails ({'{{supportLink}}'} merge tag) to invite contributions.</div>
          </CardContent></Card>
        ) : (
          <div className="space-y-2 max-w-2xl">
            {data.items.map(i => (
              <div key={i.id} data-testid={`contribution-${i.id}`} className="flex items-center gap-3 p-3 rounded-md border border-stone-200 bg-white">
                <span className="h-9 w-9 rounded-full grid place-items-center text-white text-[11px] font-bold shrink-0" style={{ background: '#722F37' }}>{i.initials.replace(/\./g, '').slice(0, 3)}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-medium text-stone-900">{i.initials}</div>
                  <div className="text-[11.5px] text-stone-500">{i.tierName}{i.date ? ` · ${new Date(i.date).toLocaleDateString()}` : ''}</div>
                </div>
                <div className="text-[15px] font-semibold text-stone-900">${i.amount.toFixed(2)}{i.interval === 'month' ? <span className="text-[11px] text-stone-500 font-normal">/mo</span> : null}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
